import moment from "moment";
import { timeFormat } from "../../utils/constants";
import {
  convertUTCTimeToLocalTime,
  getTimeZoneAbbreviation,
} from "../../utils/dateUtils";
import { fuzzyMatchScore, removeDuplicates } from "../../utils/helperFunctions";

export const ALL_FILTER = "ALL";

export const statusEnum = [
  "RUNNING",
  "HIBERNATED",
  "FAILED",
  "OTHERS",
  "TOTAL",
];

export const envFilterCacheKeys = {
  searchHistory: "envs-search-history",
  status: "envs-status-filter",
  createdBy: "envs-created-by-filter",
  columnVisibility: "envs-column-visibility",
  columnOrder: "envs-column-order",
};

// Release labels look like "0.149.0-111" (release "0.149.0", patch 111) or
// plain "0.148.0" with no patch. Plain string comparison misorders them once
// any segment reaches double digits (e.g. "0.9.0" > "0.10.0" as strings), so
// each segment is compared numerically; a release with no patch sorts before
// any of its patches.
const parseReleaseLabel = (label) => {
  if (!label) return null;
  const [base, patch] = String(label).split("-");
  return {
    version: base.split(".").map((part) => parseInt(part, 10) || 0),
    patch: patch !== undefined ? parseInt(patch, 10) || 0 : -1,
  };
};

export const compareReleaseLabels = (a, b) => {
  const parsedA = parseReleaseLabel(a);
  const parsedB = parseReleaseLabel(b);
  if (!parsedA && !parsedB) return 0;
  if (!parsedA) return -1;
  if (!parsedB) return 1;

  const length = Math.max(parsedA.version.length, parsedB.version.length);
  for (let i = 0; i < length; i++) {
    const diff = (parsedA.version[i] || 0) - (parsedB.version[i] || 0);
    if (diff) return diff;
  }
  return parsedA.patch - parsedB.patch;
};

export const isValidRequestCPPayload = (payload) => {
  return (
    Object.keys(payload).filter((key) => payload[key] === "").length === 0 &&
    !getJiraIdError(payload?.jiraId)
  );
};

// Format check only; an empty Jira Id is caught by the required-field check above.
export const getJiraIdError = (jiraId) => {
  if (!jiraId) return "";
  if (jiraId.length < 2) return "Jira Id must be at least 2 characters";
  return /^[a-zA-Z0-9-]+$/.test(jiraId)
    ? ""
    : "Only letters, numbers and hyphens are allowed";
};

export const roles = {
  OWNER: "TENANT_ENVIRONMENT_OWNER",
  LEAD: "TENANT_ENVIRONMENT_LEAD",
  DEVELOPER: "TENANT_ENVIRONMENT_DEVELOPER",
};

export const notifyMinOptions = [10, 15, 20, 30, 45, 60];

// "sandeep.kumar@tessell.com" -> "SK"; falls back to the first two characters.
export const getMemberInitials = (email) => {
  const local = (email || "").split("@")[0];
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
};

const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getRepeatLabel = (repeat) => {
  const days = Array.isArray(repeat) ? repeat : [];
  const selected = dayLabels.filter((_, idx) => days[idx]);
  if (!selected.length) return "";
  if (selected.length === 7) return "Everyday";
  if (days.slice(0, 5).every(Boolean) && !days[5] && !days[6])
    return "Weekdays";
  return selected.join(", ");
};

// Schedules are stored as 24h "HH:mm"; everything user-facing reads 12h.
// "21:00" -> "9:00 PM". Returns "--:--" when the schedule is missing.
export const formatSchedule12Hour = (schedule) => {
  const [hour, minute] = (schedule || "").split(":");
  const hours = Number(hour);
  if (hour === undefined || minute === undefined || Number.isNaN(hours))
    return "--:--";
  const meridiem = hours < 12 ? "AM" : "PM";
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minute} ${meridiem}`;
};

// "Everyday · 9:00 PM IST", "Weekdays · 9:00 AM IST", "Mon, Wed · 9:00 AM IST".
export const getScheduleSummary = (schedule, repeat) => {
  const when = getRepeatLabel(repeat);
  if (!when) return "No days selected";
  return `${when} · ${formatSchedule12Hour(
    schedule
  )} ${getTimeZoneAbbreviation()}`;
};

// "21:00" -> 1260; null when the schedule is missing or malformed.
const getScheduleMinutes = (schedule) => {
  const [hour, minute] = (schedule || "").split(":");
  const hours = Number(hour);
  const minutes = Number(minute);
  if (
    hour === undefined ||
    minute === undefined ||
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  )
    return null;
  return hours * 60 + minutes;
};

const formatScheduleMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return formatSchedule12Hour(
    `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`
  );
};

// "Hibernate 9:00 PM → Wake up 9:00 AM · ~12 hrs hibernated per day"; empty when
// either schedule is missing or the two times leave no downtime window.
export const getDowntimeSummary = (hibernateSchedule, wakeupSchedule) => {
  const hibernateMinutes = getScheduleMinutes(hibernateSchedule);
  const wakeupMinutes = getScheduleMinutes(wakeupSchedule);
  if (hibernateMinutes === null || wakeupMinutes === null) return "";
  const downtimeMinutes = (wakeupMinutes - hibernateMinutes + 1440) % 1440;
  if (!downtimeMinutes) return "";
  return `Hibernate ${formatScheduleMinutes(
    hibernateMinutes
  )} → Wake up ${formatScheduleMinutes(wakeupMinutes)} · ~${Math.round(
    downtimeMinutes / 60
  )} hrs hibernated per day`;
};

// Reads back the custom day selection under the day toggles.
export const getSelectedDaysLabel = (repeat) => {
  const days = Array.isArray(repeat) ? repeat : [];
  const selected = dayLabels.filter((_, idx) => days[idx]);
  if (!selected.length) return "No days selected";
  if (selected.length === 7) return "Every day of the week";
  if (!days[5] && !days[6]) return `${selected.join(", ")} · skips weekends`;
  return selected.join(", ");
};

export const parseRequestCPPayload = (payload) => {
  return Object.keys(payload).reduce(
    (acc, key) => (payload[key] ? { ...acc, [key]: payload[key] } : acc),
    {}
  );
};

export const getEnvsSimplified = (envs, users) => {
  return envs.map((env) => {
    let newEnvObj = structuredClone(env);

    newEnvObj.memberRole = env?.member_role?.replace("TENANT_ENVIRONMENT_", "");
    newEnvObj.members = {};
    newEnvObj.indexedMembers = [];
    env?.members?.forEach((memberObj) => {
      newEnvObj.members[memberObj?.email] = memberObj?.role_name?.replace(
        "TENANT_ENVIRONMENT_",
        ""
      );
      newEnvObj.indexedMembers.push(memberObj.email);
    });

    const repoPort = 5432;
    newEnvObj.repoConfig = `
      SPRING_DATASOURCE_PASSWORD=${env?.meta?.repository_info?.schema_password}
      SPRING_DATASOURCE_URL=jdbc:postgresql://${env?.meta?.repository_info?.schema_repo_tailscale_ip}:${repoPort}/${env?.meta?.repository_info?.schema_name_tessell}
      SPRING_DATASOURCE_USERNAME=${env?.meta?.repository_info?.schema_username}
      TESSELL_REPO_DATABASE_NAME=${env?.meta?.repository_info?.schema_name_tessell}
      TESSELL_REPO_DATABASE_PASSWORD=${env?.meta?.repository_info?.schema_password}
      TESSELL_REPO_DATABASE_USERNAME=${env?.meta?.repository_info?.schema_username}
      TESSELL_REPO_HOST_IP=${env?.meta?.repository_info?.schema_repo_tailscale_ip}
      TESSELL_REPO_HOST_PORT=${repoPort}
    `
      .replace(/  +/g, "")
      .replace(/^\s*[\r\n]/gm, "");

    newEnvObj.autoHibernateEnabled =
      env?.config?.hibernate?.enableAutomaticHibernation;
    newEnvObj.autoHibernateSchedule = convertUTCTimeToLocalTime(
      env?.config?.hibernate?.schedule?.utctime
    );
    newEnvObj.autoHibernateRepeat = Array(7)
      .fill(0)
      .map((_, i) => env?.config?.hibernate?.schedule?.repeat.includes(i));
    newEnvObj.autoHibernateNotify = notifyMinOptions.map((minutes) =>
      env?.config?.hibernate?.schedule?.notifyBeforeMinutes.includes(minutes)
    );

    newEnvObj.autoWakeupEnabled = env?.config?.wakeup?.enableAutomaticWakeup;
    newEnvObj.autoWakeupSchedule = convertUTCTimeToLocalTime(
      env?.config?.wakeup?.schedule?.utctime
    );
    newEnvObj.autoWakeupRepeat = Array(7)
      .fill(0)
      .map((_, i) => env?.config?.wakeup?.schedule?.repeat.includes(i));
    newEnvObj.autoWakeupNotify = notifyMinOptions.map((minutes) =>
      env?.config?.wakeup?.schedule?.notifyBeforeMinutes.includes(minutes)
    );

    newEnvObj.autoDeleteEnabled =
      !env?.config?.dbServicesAutoDelete?.skipDeleteProtected;
    newEnvObj.autoDeleteHours =
      env?.config?.dbServicesAutoDelete?.deleteAfterHours;

    newEnvObj.url = env?.meta?.tenant_api_endpoint?.replace("api.", "");

    newEnvObj.createdBy = env?.owner;

    // date_created is a UTC instant with no offset suffix; moment() alone
    // would parse it as already-local and skip the conversion entirely.
    newEnvObj.createdOn = env?.date_created
      ? `${moment
          .utc(env.date_created)
          .local()
          .format(timeFormat)} ${getTimeZoneAbbreviation()}`
      : "";
    // Raw instant; the table renders it via TimeCell.
    newEnvObj.lastModifiedAt = env?.date_modified;
    newEnvObj.age = Math.floor(
      moment.duration(Date.now() - Date.parse(env?.date_created)).asDays()
    );

    newEnvObj.addableMembers = users.filter(
      (member) =>
        !newEnvObj.indexedMembers
          .filter((email) => email in newEnvObj.members)
          .includes(member.email)
    );

    return newEnvObj;
  });
};

export const getFilterOptions = (envs, field) =>
  removeDuplicates(
    (envs || []).map((env) => env?.[field]).filter(Boolean)
  ).sort();

export const getFilteredEnvs = ({
  envs,
  searchText,
  statusFilter,
  createdByFilter,
}) => {
  const filtered = (envs || []).filter(
    (env) =>
      (statusFilter === ALL_FILTER || env?.status === statusFilter) &&
      (createdByFilter === ALL_FILTER || env?.createdBy === createdByFilter)
  );

  if (!searchText?.trim()) return filtered;

  return filtered
    .map((env) => ({ env, score: fuzzyMatchScore(env?.name, searchText) }))
    .filter(({ score }) => score !== null)
    .sort((a, b) => a.score - b.score)
    .map(({ env }) => env);
};

export const getEnvStatusCount = (envs) => {
  const statusCount = {};
  let nonOthers = 0;
  statusEnum.forEach((stat) => {
    statusCount[stat] = envs?.reduce((total, env) => {
      return total + env?.status?.includes(stat);
    }, 0);
    nonOthers += statusCount[stat];
  });
  statusCount["TOTAL"] = envs?.length;
  statusCount["OTHERS"] = envs?.length - nonOthers;
  statusCount.OTHERSList = {};
  statusCount.FAILEDList = {};
  envs?.forEach((env) => {
    if (env?.status.includes("FAILED")) {
      statusCount.FAILEDList[env?.status] =
        1 + (statusCount.FAILEDList[env?.status] || 0);
    } else if (!statusEnum.includes(env?.status)) {
      statusCount.OTHERSList[env?.status] =
        1 + (statusCount.OTHERSList[env?.status] || 0);
    }
  });
  return statusCount;
};
